<template>
  <div class="c-table rounded-2 pa-2 bg-base">
    <div
      ref="tableScroller"
      class="c-table__scroller"
      :class="scrollerClass ?? 'w-full overflow-auto max-h-[calc(100vh-350px)]'"
      :style="loading || (!loading && !table.getRowModel().rows.length) ? {} : scrollerStyle"
    >
      <table
        v-if="!loading"
        ref="tableElement"
        v-el-mounted="onTableMounted"
        aria-label="c-table"
        class="w-full border-1 border-collapse"
        :style="{ tableLayout: enableColumnResizing && hasInitializedSizes ? 'fixed' : 'auto' }"
      >
        <thead v-if="!!table.getRowModel().rows.length">
          <tr>
            <th v-if="selectedRows && selectable" class="sticky top-0 z-2 overflow-hidden w-[26px]">
              <div
                class="py-2 h-[40px] flex pl-3 items-center select-none bg-base"
                :style="{ borderBottom: `1px solid ${themeVars.primary5}` }"
              >
                <CCheckbox
                  v-tooltip="
                    areAllSelected
                      ? t('canvasUI.components.CTable.deselectAll')
                      : t('canvasUI.components.CTable.selectAll')
                  "
                  :checked="areAllSelected"
                  :indeterminate="!isNoneSelected && !areAllSelected"
                  @click.stop="handleSelectAll"
                />
              </div>
            </th>
            <th
              v-for="header in table.getFlatHeaders()"
              :key="header.id"
              :class="[
                'sticky top-0 z-2 overflow-hidden group',
                header.column.getCanSort() ? 'cursor-pointer' : '',
                ...getClasses(header.column.columnDef),
              ]"
              :style="{
                width: enableColumnResizing && hasInitializedSizes ? `${header.getSize()}px` : undefined,
              }"
              @click="handleSort(header)"
            >
              <div
                class="px-4 py-2 select-none flex items-center gap-1 h-[40px] bg-base"
                :style="{
                  borderBottom: `1px solid ${themeVars.primary5}`,
                }"
              >
                <FlexRender :props="header.getContext()" :render="header.column.columnDef.header" />
                <CIcon
                  v-if="header.column.getCanSort() && header.column.getIsSorted()"
                  class="ml-1 opacity-70 group-hover:opacity-100 transition-opacity"
                  :color="themeVars.primary2"
                  :icon="header.column.getIsSorted() === 'desc' ? 'arrow-down-long' : 'arrow-up-long'"
                  size="16px"
                />
                <CIcon
                  v-else-if="header.column.getCanSort()"
                  class="ml-1 opacity-50 group-hover:opacity-100 transition-opacity"
                  :color="themeVars.primary4"
                  icon="arrows-up-down"
                  size="16px"
                />
              </div>
              <div
                v-if="launchDarkly.allow_table_column_resizing"
                class="c-table__resizer"
                :class="{ 'c-table__resizer--resizing': header.column.getIsResizing() }"
                :style="{
                  position: 'absolute',
                  right: '0',
                  top: '8px',
                  bottom: 0,
                  height: '20px',
                  width: '4px',
                  cursor: 'col-resize',
                  userSelect: 'none',
                  touchAction: 'none',
                  borderRight: `solid ${themeVars.primary5}`,
                  backgroundColor: header.column.getIsResizing() ? themeVars.primary : 'transparent',
                }"
                @click.stop.prevent
                @mousedown.stop="handleResizeStart($event, header)"
                @touchstart.stop="handleResizeStart($event, header)"
              />
            </th>
          </tr>
        </thead>
        <tbody v-if="!!table.getRowModel().rows.length">
          <tr
            v-for="(row, idx) in table.getRowModel().rows"
            :key="row.id"
            :style="{
              borderBottom: `1px solid ${themeVars.primary6}`,
              backgroundColor: !!(idx % 2) ? themeVars.primary6_1 : themeVars.base,
            }"
          >
            <td v-if="selectedRows && selectable" class="pl-3 w-[26px]">
              <CCheckbox
                :checked="!!selectedRows[row.original.id]"
                :disabled="isRowDisabled(row.original.id)"
                @click="handleSelectRow(row.original)"
              />
            </td>
            <td
              v-for="cell in row.getVisibleCells()"
              :key="cell.id"
              :class="[
                'pl-4',
                'py-2',
                'min-w-[100px]',
                'max-w-[400px]',
                'overflow-hidden',
                'text-ellipsis',
                'whitespace-nowrap',
                ...getClasses(cell.column.columnDef),
              ]"
              :style="{
                borderBottom: `1px solid ${themeVars.primary6}`,
              }"
            >
              <FlexRender class="max-w-full" :props="cell.getContext()" :render="cell.column.columnDef.cell" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <template v-if="!loading">
      <div
        v-if="isTableEmpty"
        class="flex items-center justify-center font-size-[16px] tracking-[1.25px] color-primary2"
      >
        {{ t('canvasUI.common.noData') }}
      </div>
      <div
        v-if="isDataAvailable && !hidePagination"
        class="c-table__pagination flex items-center justify-between px-4 pt-4"
      >
        <div v-if="!hidePageSize" class="flex items-center">
          <CSelect
            :on-update:value="handlePageSizeChange"
            :options="pageSizeOptions"
            :value="table.getState().pagination.pageSize"
          />
        </div>
        <div v-if="!hideTotalResults" class="font-500 tracking-[1.25px] color-primary2">
          {{ t('canvasUI.components.CTable.countResults', { count }) }}
        </div>
        <div v-if="!hidePageSelector" class="flex items-center gap-1">
          <CButton
            class="border-1 rounded w-[36px] h-[36px]"
            :disabled="!table.getCanPreviousPage()"
            @click="
              () => {
                table.previousPage()
                emit('pageChanged', table.getState().pagination)
              }
            "
          >
            <template #icon>
              <CIcon icon="angle-left" size="16px" />
            </template>
          </CButton>
          <div
            class="flex items-center px-2 gap-1 h-[36px] rounded font-size-[16px] tracking-[1.25px]"
            :style="{ backgroundColor: themeVars.primary6, color: themeVars.primary }"
          >
            {{
              `${t('canvasUI.components.CTable.page')} ${
                table.getState().pagination.pageIndex + 1
              } / ${table.getPageCount()}`
            }}
          </div>
          <CButton
            class="border-1 rounded w-[36px] h-[36px]"
            :disabled="!table.getCanNextPage()"
            @click="
              () => {
                table.nextPage()
                emit('pageChanged', table.getState().pagination)
              }
            "
          >
            <template #icon>
              <CIcon icon="angle-right" size="16px" />
            </template>
          </CButton>
        </div>
      </div>
    </template>
    <div v-else class="flex items-center justify-center pt-4 pb-4">
      <NSpin size="large" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  type SortingState,
  type PaginationState,
  type ColumnDef,
  type Header,
  type ColumnSizingState,
} from '@tanstack/table-core'
import { FlexRender, useVueTable } from '@tanstack/vue-table'
import { ref, watch, computed, inject, type ComputedRef, type StyleValue } from 'vue'
import { useI18n } from 'vue-i18n'
import { NSpin } from 'naive-ui'

import CSelect from '@lib/components/CSelect/CSelect.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CCheckbox from '@lib/components/CCheckbox/CCheckbox.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '@lib/constants/tables.const'
import tooltipDirective from '@lib/directives/tooltip'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

type RowItem = Record<string, any>

const emit = defineEmits<{
  sortChanged: [s: SortingState]
  pageChanged: [p: PaginationState]
  pageSizeChanged: [p: number]
  'update:selectedRows': [s: Record<string, boolean>]
}>()

const props = withDefaults(
  defineProps<{
    selectedRows?: Record<string, boolean>
    scrollerClass?: string
    columns: ColumnDef<RowItem, any>[]
    data: RowItem[]
    initialSorting?: SortingState
    loading?: boolean
    hidePageSelector?: boolean
    hidePageSize?: boolean
    hidePagination?: boolean
    hideTotalResults?: boolean
    pageSize?: number
    pageSizes?: number[]
    pageIndex?: number
    totalCount?: number
    displayedCount?: number //Temporary: Only when using serverSide with Client side filters
    serverSide?: boolean
    scrollerStyle?: StyleValue
    selectable?: boolean
    disabledIds?: Array<string | number>
  }>(),
  {
    data: () => [],
    columns: () => [],
    hidePageSelector: false,
    hidePageSize: false,
    hidePagination: false,
    hideTotalResults: false,
    initialSorting: () => [{ desc: false, id: 'name' }],
    loading: false,
    pageSize: DEFAULT_PAGE_SIZE,
    pageIndex: 0,
    serverSide: false,
    disabledIds: () => [],
  },
)
const { t } = useI18n()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const enableColumnResizing = computed(() => !!launchDarkly.value.allow_table_column_resizing)

const pageSizeOptions = computed(() => {
  const sizesToUse = props.pageSizes?.length ? props.pageSizes : DEFAULT_PAGE_SIZE_OPTIONS

  return sizesToUse.map((s) => ({
    label: `${s} / ${t('canvasUI.components.CTable.page')}`,
    value: s,
  }))
})
const sorting = ref<SortingState>(props.initialSorting)
const pagination = ref<PaginationState>({ pageIndex: 0, pageSize: props.pageSize })
const columnSizing = ref<ColumnSizingState>({})
const isNoneSelected = computed<boolean>(
  () => !props.selectedRows || Object.keys(props.selectedRows).every((k) => !props.selectedRows?.[k]),
)

const areAllSelected = computed<boolean>(() => {
  const currentPageRowIds = table
    .getRowModel()
    .rows.slice(0, table.getState().pagination.pageSize)
    .map((r) => r.original.id)
    .filter((id) => !isRowDisabled(id))

  return currentPageRowIds.length > 0 && currentPageRowIds.every((id) => props.selectedRows?.[id])
})

const selectedRowsCount = computed(() => {
  return Object.keys(props.selectedRows ?? {}).filter((key) => props.selectedRows?.[key]).length
})

//#region client-side filtering with server-side pagination - Note: Remove when we have server-side filtering
const count = computed(() =>
  props.displayedCount !== undefined ? props.displayedCount : props.serverSide ? props.totalCount : props.data.length,
)

const isTableEmpty = computed(() => !table.getRowModel().rows.length || props.totalCount === 0)
const isDataAvailable = computed(() => (props.totalCount ?? 0) > 0 || table.getRowModel().rows.length > 0)
//#endregion

const table = useVueTable({
  autoResetPageIndex: false,
  get enableColumnResizing() {
    return enableColumnResizing.value
  },
  columnResizeMode: 'onChange',
  defaultColumn: {
    minSize: 120,
    maxSize: 1000,
  },
  get columns() {
    return props.columns
  },
  get data() {
    return props.data
  },
  state: {
    get sorting() {
      return sorting.value
    },
    get pagination() {
      return pagination.value
    },
    get columnSizing() {
      return columnSizing.value
    },
  },
  onSortingChange: (updaterOrValue) => {
    sorting.value = typeof updaterOrValue === 'function' ? updaterOrValue(sorting.value) : updaterOrValue
  },
  onPaginationChange: (updaterOrValue) => {
    pagination.value = typeof updaterOrValue === 'function' ? updaterOrValue(pagination.value) : updaterOrValue
  },
  onColumnSizingChange: (updaterOrValue) => {
    columnSizing.value = typeof updaterOrValue === 'function' ? updaterOrValue(columnSizing.value) : updaterOrValue
  },
  getCoreRowModel: getCoreRowModel(),
  ...(props.serverSide
    ? {
        pageCount: props.totalCount ? Math.ceil(props.totalCount / props.pageSize) : -1,
        manualPagination: true,
        manualSorting: true,
      }
    : {
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
      }),
})

table.setPageSize(props.pageSize)

function isRowDisabled(id: string | number): boolean {
  if (!props.disabledIds?.length) return false
  return props.disabledIds.includes(id as any)
}

function allPageRows(v = true) {
  return table.getRowModel().rows.reduce(
    (acc, r) => {
      // Skip disabled rows
      if (!isRowDisabled(r.original.id)) {
        acc[r.original.id] = v
      } else if (props.selectedRows?.[r.original.id] !== undefined) {
        // Preserve existing selection state for disabled rows
        acc[r.original.id] = props.selectedRows[r.original.id]
      }
      return acc
    },
    {} as Record<string, boolean>,
  )
}

function handleSelectAll() {
  emit('update:selectedRows', areAllSelected.value ? allPageRows(false) : allPageRows(true))
}

function handleSelectRow(row: RowItem) {
  // Prevent selection of disabled rows
  if (isRowDisabled(row.id)) return

  emit('update:selectedRows', {
    ...props.selectedRows,
    [row.id]: !props.selectedRows?.[row.id],
  })
}

function getClasses(c: ColumnDef<RowItem, any> & { meta?: { classes?: string } }) {
  return c.meta?.classes ? [c.meta?.classes] : []
}

const tableScroller = ref<HTMLElement | null>(null)
const tableElement = ref<HTMLTableElement | null>(null)
let savedScrollPosition = { left: 0, top: 0 }
const resizingColumnId = ref<string | null>(null)
const hasInitializedSizes = ref(false)

function handleSort(header: Header<RowItem, unknown>) {
  if (!header.column.getCanSort()) return
  if (resizingColumnId.value === header.column.id) return

  const currentSort = header.column.getIsSorted()
  if (currentSort === 'asc') {
    header.column.toggleSorting(true)
  } else if (currentSort === 'desc') {
    header.column.clearSorting()
  } else {
    header.column.toggleSorting(false)
  }

  emit('sortChanged', sorting.value)
  !props.serverSide && table.setPageIndex(0)
}

function handleResizeStart(event: MouseEvent | TouchEvent, header: Header<RowItem, unknown>) {
  if (!hasInitializedSizes.value && tableElement.value) {
    const headers = tableElement.value.querySelectorAll('thead th')
    if (headers) {
      const sizingState: ColumnSizingState = {}
      const flatHeaders = table.getFlatHeaders()

      flatHeaders.forEach((header, index) => {
        const offset = props.selectable && props.selectedRows ? 1 : 0
        const headerElement = headers[index + offset] as HTMLElement
        if (headerElement && header.id) {
          const width = headerElement.offsetWidth
          sizingState[header.id] = width
        }
      })

      if (Object.keys(sizingState).length > 0) {
        columnSizing.value = sizingState
        hasInitializedSizes.value = true
      }
    }
  }

  const resizeHandler = header.getResizeHandler()
  if (resizeHandler) {
    resizingColumnId.value = header.column.id
    resizeHandler(event)

    const handleResizeEnd = () => {
      document.removeEventListener('mouseup', handleResizeEnd)
      document.removeEventListener('touchend', handleResizeEnd)
      document.removeEventListener('touchcancel', handleResizeEnd)

      setTimeout(() => {
        resizingColumnId.value = null
      }, 100)
    }

    document.addEventListener('mouseup', handleResizeEnd, { once: true })
    document.addEventListener('touchend', handleResizeEnd, { once: true })
    document.addEventListener('touchcancel', handleResizeEnd, { once: true })
  }
}

function onTableMounted() {
  restoreScrollPosition()
}

function restoreScrollPosition() {
  if (tableScroller.value && (savedScrollPosition.left > 0 || savedScrollPosition.top > 0)) {
    void tableScroller.value.offsetHeight

    tableScroller.value.scrollLeft = savedScrollPosition.left
    tableScroller.value.scrollTop = savedScrollPosition.top
    savedScrollPosition = { left: 0, top: 0 }
  }
}

watch(
  () => props.loading,
  (v) => {
    if (v) saveScrollPosition()
  },
)

function saveScrollPosition() {
  if (tableScroller.value) {
    savedScrollPosition = {
      left: tableScroller.value.scrollLeft || 0,
      top: tableScroller.value.scrollTop || 0,
    }
  }
}

function handlePageSizeChange(pageSize: number) {
  if (pageSize === table.getState().pagination.pageSize) return

  table.setPageSize(pageSize)
  emit('pageSizeChanged', pageSize)
  !props.serverSide && table.setPageIndex(0)
}

if (props.serverSide) {
  watch(
    () => props.totalCount,
    (v) => {
      // TODO: use table.setPageCount when it's fixed on the upstream
      if (v) {
        table.setOptions((opt) => {
          return {
            ...opt,
            get data() {
              return props.data
            },
            pageCount: Math.ceil(v / table.getState().pagination.pageSize),
          }
        })
        emit('update:selectedRows', {})
      }
    },
  )

  watch(
    () => props.pageSize,
    (pageSize) => {
      const tc = props.totalCount
      if (tc) {
        table.setOptions((opt) => ({
          ...opt,
          get data() {
            return props.data
          },
          pageCount: Math.ceil(tc / pageSize),
        }))
        emit('update:selectedRows', {})
      }
    },
  )

  watch(
    () => props.pageIndex,
    (v) => {
      savedScrollPosition = { left: 0, top: 0 }
      if (v !== table.getState().pagination.pageIndex) {
        table.setPageIndex(v)
        emit('update:selectedRows', {})
      }
    },
  )
} else {
  watch(
    () => props.data,
    () => {
      const currentPageIndex = table.getState().pagination.pageIndex
      const activePageChunk = table.getPaginationRowModel().flatRows

      if (activePageChunk.length === 0 && currentPageIndex > 0) {
        table.setPageIndex(0)
      }
    },
  )
}

watch(
  () => props.columns,
  () => {
    table.setOptions((opt) => ({
      ...opt,
      get data() {
        return props.data
      },
      get columns() {
        return props.columns
      },
    }))
  },
  { deep: true },
)

defineExpose({
  isNoneSelected,
  areAllSelected,
  selectAll: handleSelectAll,
  selectedRowsCount,
  pageRowsCount: computed(() => table.getRowModel().rows.length),
  isRowDisabled,
})
</script>

<style scoped>
.c-table__resizer:hover {
  background-color: v-bind('themeVars.primary3');
  border-right-color: v-bind('themeVars.primary2');
}

.c-table__resizer--resizing {
  background-color: v-bind('themeVars.primary') !important;
  border-right-color: v-bind('themeVars.primary') !important;
}
</style>
